import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { get as apiGet, del as apiDel } from '../services/api/apiClient';
import MainLayout from '../layouts/MainLayout';

function LibraryPage() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    loadMedia();
    loadStats();
  }, []);

  const loadMedia = async () => {
    try {
      setIsLoading(true);
      const response = await apiGet('/media?limit=1000');
      if (response.success) {
        setMediaFiles(response.media || []);
      }
    } catch (error) {
      console.error('Failed to load media:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiGet('/media/stats');
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleDeleteFile = async (mediaId) => {
    if (!window.confirm(t('libraryPage.deleteConfirm'))) {
      return;
    }

    try {
      const response = await apiDel(`/media/${mediaId}`);
      if (response.success) {
        setMediaFiles(mediaFiles.filter(file => file.id !== mediaId));
        setSelectedFile(null);
        loadStats();
      } else {
        alert(t('libraryPage.deleteFailed'));
      }
    } catch (error) {
      console.error('Failed to delete media:', error);
      alert(t('libraryPage.deleteFailed'));
    }
  };

  // 按日期分組檔案
  const groupByDate = (files) => {
    const grouped = {};

    files.forEach(file => {
      const date = new Date(file.created_at);
      const dateKey = date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(file);
    });

    // 按日期排序（最新的在前）
    return Object.entries(grouped).sort((a, b) => {
      return new Date(b[1][0].created_at) - new Date(a[1][0].created_at);
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return 'mdi:image';
    if (type?.startsWith('video/')) return 'mdi:video';
    if (type?.includes('pdf')) return 'mdi:file-pdf';
    return 'mdi:file';
  };

  const groupedFiles = groupByDate(mediaFiles);

  return (
    <MainLayout activePage="library">
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div
          className="px-10 py-5 flex items-center justify-between"
          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
        >
            <h1 className="m-0 text-[1.75rem] font-bold">
              {t('libraryPage.library')}
            </h1>

            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div
                className="flex rounded-lg p-1"
                style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}` }}
              >
                <button
                  onClick={() => setViewMode('grid')}
                  className="px-3 py-1.5 border-none rounded-md cursor-pointer flex items-center"
                  style={{
                    background: viewMode === 'grid' ? theme.buttonPrimary : 'transparent',
                    color: viewMode === 'grid' ? theme.buttonPrimaryText : theme.textSecondary
                  }}
                >
                  <Icon icon="mdi:view-grid" className="text-lg" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className="px-3 py-1.5 border-none rounded-md cursor-pointer flex items-center"
                  style={{
                    background: viewMode === 'list' ? theme.buttonPrimary : 'transparent',
                    color: viewMode === 'list' ? theme.buttonPrimaryText : theme.textSecondary
                  }}
                >
                  <Icon icon="mdi:view-list" className="text-lg" />
                </button>
              </div>

              <button
                className="px-5 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer flex items-center gap-2"
                style={{ background: theme.buttonPrimary, color: theme.buttonPrimaryText }}
              >
                <Icon icon="mdi:upload" className="text-xl" />
                {t('libraryPage.upload')}
              </button>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto p-10">
            {isLoading ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <Icon icon="mdi:loading" className="animate-spin text-5xl" style={{ color: theme.textTertiary }} />
              </div>
            ) : mediaFiles.length === 0 ? (
              <div className="text-center py-20 px-5" style={{ color: theme.textTertiary }}>
                <Icon icon="mdi:folder-open-outline" className="text-6xl mb-4" />
                <h3 className="m-0 mb-2" style={{ color: theme.textSecondary }}>{t('libraryPage.noFiles')}</h3>
                <p className="m-0 text-sm">{t('libraryPage.uploadFirst')}</p>
              </div>
            ) : (
              groupedFiles.map(([dateKey, files]) => (
                <div key={dateKey} className="mb-12">
                  {/* Date Header */}
                  <h2
                    className="m-0 mb-5 text-xl font-semibold flex items-center gap-3"
                    style={{ color: theme.textPrimary }}
                  >
                    <Icon icon="mdi:calendar" className="text-2xl" style={{ color: theme.textTertiary }} />
                    {dateKey}
                    <span className="text-sm font-normal" style={{ color: theme.textTertiary }}>
                      ({files.length} {t(files.length === 1 ? 'libraryPage.file' : 'libraryPage.files')})
                    </span>
                  </h2>

                  {/* Files Grid or List */}
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
                      {files.map(file => (
                        <FileCard
                          key={file.id}
                          file={file}
                          theme={theme}
                          onClick={() => setSelectedFile(file)}
                          onDelete={() => handleDeleteFile(file.id)}
                          formatFileSize={formatFileSize}
                          getFileIcon={getFileIcon}
                          t={t}
                        />
                      ))}
                    </div>
                  ) : (
                    <div
                      className="rounded-xl overflow-hidden"
                      style={{ border: `1px solid ${theme.borderColor}` }}
                    >
                      {files.map((file, index) => (
                        <FileListItem
                          key={file.id}
                          file={file}
                          theme={theme}
                          isLast={index === files.length - 1}
                          onClick={() => setSelectedFile(file)}
                          onDelete={() => handleDeleteFile(file.id)}
                          formatFileSize={formatFileSize}
                          getFileIcon={getFileIcon}
                          t={t}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

        {/* File Preview Modal */}
        {selectedFile && (
          <FilePreviewModal
            file={selectedFile}
            theme={theme}
            onClose={() => setSelectedFile(null)}
            onDelete={() => handleDeleteFile(selectedFile.id)}
            formatFileSize={formatFileSize}
            t={t}
          />
        )}
      </div>
    </MainLayout>
  );
}

function FileCard({ file, theme, onClick, onDelete, formatFileSize, getFileIcon, t }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      className="relative rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
      style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
    >
      {/* Preview */}
      <div
        onClick={onClick}
        className="w-full h-40 flex items-center justify-center overflow-hidden"
        style={{ background: theme.bgPrimary }}
      >
        {file.type?.startsWith('image/') && file.cloudinary_secure_url ? (
          <img
            src={file.cloudinary_secure_url}
            alt={file.original_filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <Icon
            icon={getFileIcon(file.type)}
            className="text-5xl"
            style={{ color: theme.textTertiary }}
          />
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <div
          className="text-[13px] font-medium mb-1 overflow-hidden text-ellipsis whitespace-nowrap"
          style={{ color: theme.textPrimary }}
        >
          {file.original_filename || t('libraryPage.untitled')}
        </div>
        <div className="text-xs" style={{ color: theme.textTertiary }}>
          {formatFileSize(file.size_bytes)}
        </div>
      </div>

      {/* Menu Button */}
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="bg-black/50 border-none rounded-md p-1.5 cursor-pointer flex items-center text-white"
        >
          <Icon icon="mdi:dots-vertical" className="text-lg" />
        </button>

        {showMenu && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute top-full right-0 mt-1 rounded-lg shadow-lg min-w-[150px] z-[100] overflow-hidden"
            style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(file.cloudinary_secure_url, '_blank');
                setShowMenu(false);
              }}
              className="w-full px-3 py-2.5 bg-transparent border-none text-left cursor-pointer flex items-center gap-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{ color: theme.textPrimary }}
            >
              <Icon icon="mdi:open-in-new" className="text-base" />
              {t('libraryPage.open')}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(file.cloudinary_secure_url);
                setShowMenu(false);
              }}
              className="w-full px-3 py-2.5 bg-transparent border-none text-left cursor-pointer flex items-center gap-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{ color: theme.textPrimary }}
            >
              <Icon icon="mdi:content-copy" className="text-base" />
              {t('libraryPage.copyUrl')}
            </button>
            <div className="h-px my-1" style={{ background: theme.borderColor }} />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(false);
                onDelete();
              }}
              className="w-full px-3 py-2.5 bg-transparent border-none text-left cursor-pointer flex items-center gap-2 text-sm text-red-500 hover:bg-red-50"
            >
              <Icon icon="mdi:delete" className="text-base" />
              {t('libraryPage.delete')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FileListItem({ file, theme, isLast, onClick, onDelete, formatFileSize, getFileIcon, t }) {
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-4 p-4 cursor-pointer transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-800"
      style={{ borderBottom: isLast ? 'none' : `1px solid ${theme.borderColor}` }}
    >
      {/* Thumbnail */}
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden shrink-0"
        style={{ background: theme.bgPrimary }}
      >
        {file.type?.startsWith('image/') && file.cloudinary_secure_url ? (
          <img
            src={file.cloudinary_secure_url}
            alt={file.original_filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <Icon icon={getFileIcon(file.type)} className="text-2xl" style={{ color: theme.textTertiary }} />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-medium mb-1 overflow-hidden text-ellipsis whitespace-nowrap"
          style={{ color: theme.textPrimary }}
        >
          {file.original_filename || t('libraryPage.untitled')}
        </div>
        <div className="text-xs" style={{ color: theme.textTertiary }}>
          {formatFileSize(file.size_bytes)} • {file.type || 'Unknown type'}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(file.cloudinary_secure_url);
          }}
          className="p-2 bg-transparent rounded-md cursor-pointer"
          style={{ border: `1px solid ${theme.borderColor}`, color: theme.textSecondary }}
        >
          <Icon icon="mdi:content-copy" className="text-lg" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-2 bg-transparent rounded-md cursor-pointer text-red-500 border border-red-500"
        >
          <Icon icon="mdi:delete" className="text-lg" />
        </button>
      </div>
    </div>
  );
}

function FilePreviewModal({ file, theme, onClose, onDelete, formatFileSize, t }) {
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-5"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl max-w-[900px] max-h-[90vh] w-full overflow-auto flex flex-col"
        style={{ background: theme.bgSecondary }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-5"
          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
        >
          <h3
            className="m-0 text-xl font-semibold overflow-hidden text-ellipsis whitespace-nowrap flex-1 pr-5"
            style={{ color: theme.textPrimary }}
          >
            {file.original_filename || t('libraryPage.untitled')}
          </h3>
          <button
            onClick={onClose}
            className="bg-transparent border-none cursor-pointer p-2 flex items-center"
            style={{ color: theme.textSecondary }}
          >
            <Icon icon="mdi:close" className="text-2xl" />
          </button>
        </div>

        {/* Preview */}
        <div
          className="flex-1 flex items-center justify-center p-10 min-h-[400px]"
          style={{ background: theme.bgPrimary }}
        >
          {file.type?.startsWith('image/') && file.cloudinary_secure_url ? (
            <img
              src={file.cloudinary_secure_url}
              alt={file.original_filename}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          ) : (
            <div className="text-center" style={{ color: theme.textTertiary }}>
              <Icon icon="mdi:file" className="text-6xl mb-4" />
              <p>{t('libraryPage.previewNotAvailable')}</p>
            </div>
          )}
        </div>

        {/* Info & Actions */}
        <div className="p-5" style={{ borderTop: `1px solid ${theme.borderColor}` }}>
          <div className="grid grid-cols-2 gap-4 mb-5 text-sm">
            <div>
              <div className="mb-1" style={{ color: theme.textTertiary }}>{t('libraryPage.fileSize')}</div>
              <div className="font-medium" style={{ color: theme.textPrimary }}>
                {formatFileSize(file.size_bytes)}
              </div>
            </div>
            <div>
              <div className="mb-1" style={{ color: theme.textTertiary }}>{t('libraryPage.type')}</div>
              <div className="font-medium" style={{ color: theme.textPrimary }}>
                {file.type || 'Unknown'}
              </div>
            </div>
            {file.width && file.height && (
              <div>
                <div className="mb-1" style={{ color: theme.textTertiary }}>{t('libraryPage.dimensions')}</div>
                <div className="font-medium" style={{ color: theme.textPrimary }}>
                  {file.width} × {file.height}
                </div>
              </div>
            )}
            <div>
              <div className="mb-1" style={{ color: theme.textTertiary }}>{t('libraryPage.uploaded')}</div>
              <div className="font-medium" style={{ color: theme.textPrimary }}>
                {new Date(file.created_at).toLocaleDateString('zh-TW')}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => window.open(file.cloudinary_secure_url, '_blank')}
              className="flex-1 px-5 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer flex items-center justify-center gap-2"
              style={{ background: theme.buttonPrimary, color: theme.buttonPrimaryText }}
            >
              <Icon icon="mdi:open-in-new" className="text-lg" />
              {t('libraryPage.open')}
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(file.cloudinary_secure_url);
                alert(t('libraryPage.urlCopied'));
              }}
              className="flex-1 px-5 py-2.5 rounded-lg text-sm font-medium cursor-pointer flex items-center justify-center gap-2"
              style={{ background: theme.buttonSecondary, color: theme.textPrimary, border: `1px solid ${theme.borderColor}` }}
            >
              <Icon icon="mdi:content-copy" className="text-lg" />
              {t('libraryPage.copyUrl')}
            </button>
            <button
              onClick={() => {
                onDelete();
                onClose();
              }}
              className="px-5 py-2.5 bg-transparent text-red-500 border border-red-500 rounded-lg text-sm font-medium cursor-pointer flex items-center gap-2"
            >
              <Icon icon="mdi:delete" className="text-lg" />
              {t('libraryPage.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// NavItem Component
export default LibraryPage;
